import { Close, Delete, Edit, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import regionService from "../../services/region";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function RegionList() {
  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({ keyword: "" });
  const notification = useNotification();
  const alert = useAlert();

  const form = useForm({
    defaultValues: { keyword: "" },
  });

  const listRegion = useQuery(
    ["region-list", options],
    ({ queryKey }) => regionService.getAllRegions(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const deleteRegion = useMutation(regionService.deleteRegion, {
    onSuccess: () => {
      listRegion.refetch();
      notification.onOpen({
        message: "Region was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onDelete = (id: number) => {
    alert.onClose();
    deleteRegion.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure delete this region?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onHandleSearch = useCallback((values: { keyword: string }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const columns: any = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: ({ row }: any) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Edit />}
            component={RouterLink}
            to={`/app/region/edit/${row.id}`}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => onClickDelete(row.id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <MainLayout>
      <Helmet>
        <title>Region Management</title>
      </Helmet>

      <Breadcrumb
        label="Region Management"
        breadcrumbs={[{ label: "Dashboard", href: "/app" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button variant="outlined" onClick={form.handleSubmit(onHandleSearch)}>
              SEARCH
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" component={RouterLink} to="/app/region/create">
            Create Region
          </Button>
        </Box>

        <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
          <DataGrid
            autoHeight
            rows={listRegion.data?.data || []}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={config.DEFAULT_PAGE_SIZE}
            disableSelectionOnClick
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}